import { useEffect, useMemo, useState } from 'react'

import { useUpdateDevice } from './useUpdateDevice'
import { useSelector } from 'react-redux'

import {
  useFacilityDevicesByFloorsQuery,
  useFacilityFloorPlanQuery,
} from '@/graphql/generated/hooks'
import { DeviceType, UrlContent } from '@/graphql/generated/schemas'
import { selectFacilityDrawerOpen } from '@/redux/ui/uiSlice'

import { DeviceI, FloorI, Point } from '../types/types'
import {
  filterDevicesWithAssociations,
  getDeviceFloorIds,
  mapDevices,
} from '../utils/utils'

export const useFloorData = (
  floorId: string,
  floors: FloorI[],
  facilityId: string,
  isEditMode: boolean,
  types: DeviceType[],
  nameLikeFilter?: string,
  shouldShowAssociatedDevices?: boolean
) => {
  const [imageUrl, setImageUrl] = useState(null)
  const [devices, setDevices] = useState<DeviceI[]>([])
  const drawerOpen = useSelector(selectFacilityDrawerOpen)
  const isDevicesPanel = drawerOpen === 'devices'

  const { updateDevice } = useUpdateDevice(floorId)
  const shouldFilterByNameLike = isDevicesPanel && !shouldShowAssociatedDevices
  const {
    data: devicesData,
    loading: isDevicesLoading,
    refetch: refetchDevices,
  } = useFacilityDevicesByFloorsQuery({
    fetchPolicy: 'network-only',
    skip: facilityId === undefined,
    variables: {
      floorId: floorId,
      facilityId: facilityId,
      types: types,
      floorIds: getDeviceFloorIds(floorId, floors, types),
      ...(shouldFilterByNameLike && { nameLike: nameLikeFilter || undefined }),
    },
  })
  const { data: floorPlanData, loading: isFloorPlanLoading } =
    useFacilityFloorPlanQuery({
      skip: facilityId === undefined,
      variables: {
        floorId: floorId,
        facilityId: facilityId,
      },
    })

  const onFloorPlanContainerClick = () => {
    setDevices((currentData) =>
      currentData.map((item) => {
        return { ...item, isEditMode: false, editCheckpointPos: false }
      })
    )
  }

  const onDeviceClicked = (deviceId: string) => {
    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === deviceId) {
          return {
            ...item,
            isEditMode: !item.isEditMode,
          }
        }
        return { ...item, isEditMode: false, editCheckpointPos: false }
      })
    )
  }

  const onMoveCheckPointClicked = (deviceId: string) => {
    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === deviceId) {
          return {
            ...item,
            editCheckpointPos: !item.editCheckpointPos,
          }
        }
        return { ...item, editCheckpointPos: false }
      })
    )
  }

  const onDeviceUpdated = (data: DeviceI, positionValid: boolean) => {
    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === data.id) {
          return {
            ...data,
            invalidPosition: !positionValid,
          }
        }
        return item
      })
    )
    updateDevice(data, positionValid, false)
  }

  const onDeviceReleased = (data: DeviceI, positionValid: boolean) => {
    if (!positionValid) {
      setDevices((currentData) =>
        currentData.map((item) => {
          if (item.id === data.id) {
            return {
              ...item,
              isEditMode: false,
              placed: false,
              editCheckpointPos: false,
            }
          }
          return { ...item, isEditMode: false, editCheckpointPos: false }
        })
      )
    }
    updateDevice(data, positionValid, true)
  }

  const onDeviceDropped = (draggedDeviceId: string, position: Point) => {
    const [deviceX, deviceY] = position
    setDevices((currentData) =>
      currentData.map((item) => {
        if (item.id === draggedDeviceId) {
          return {
            ...item,
            x: deviceX,
            y: deviceY,
            isEditMode: true,
            placed: true,
            invalidPosition: false,
            checkpoint: {
              ...item.checkpoint,
              floor: {
                ...item.checkpoint.floor,
                id: floorId,
              },
            },
          }
        }
        return {
          ...item,
          isEditMode: false,
          editCheckpointPos: false,
          invalidPosition: false,
        }
      })
    )
    const deviceData = devices.find((device) => device.id === draggedDeviceId)
    if (deviceData) {
      updateDevice({ ...deviceData, x: deviceX, y: deviceY }, true, true)
    }
  }

  useEffect(() => {
    if (!isDevicesLoading && devicesData) {
      let mappedDevices = mapDevices(devicesData)

      const shouldFilterByAssociateDevices =
        isDevicesPanel && shouldShowAssociatedDevices
      // If enabled, Filter devices to include their associated devices
      if (shouldFilterByAssociateDevices) {
        mappedDevices = filterDevicesWithAssociations(
          mappedDevices,
          nameLikeFilter
        )
      }

      setDevices(mappedDevices)
    }
  }, [
    devicesData,
    isDevicesLoading,
    isDevicesPanel,
    nameLikeFilter,
    shouldShowAssociatedDevices,
  ])

  useEffect(() => {
    if (!isFloorPlanLoading && floorPlanData) {
      const floorPlanImage = floorPlanData?.facility?.floors?.edges[0]?.node
        ?.floorPlan?.image as UrlContent
      setImageUrl(floorPlanImage?.url)
    }
  }, [floorPlanData, isFloorPlanLoading])

  useEffect(() => {
    setDevices((currentData) =>
      currentData.map((item) => ({
        ...item,
        isEditMode: false,
        editCheckpointPos: false,
      }))
    )
  }, [isEditMode])

  const checkpointDevices = useMemo(
    () => devices?.filter((d) => d.deviceType === DeviceType.Checkpoint),
    [devices]
  )

  return {
    facilityId,
    imageUrl,
    devices,
    checkpointDevices,
    loading: isDevicesLoading,
    onDeviceClicked,
    onFloorPlanContainerClick,
    onDeviceUpdated,
    onDeviceReleased,
    onDeviceDropped,
    onMoveCheckPointClicked,
    refetchDevices,
  }
}
